import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Modal from '../Common/Modal';
import APIKeyManagement from './APIKeyManagement';

const APIServiceConfig = ({ userId, onClose, targetServiceId }) => {
    const [loading, setLoading] = useState(true);
    const [services, setServices] = useState({});
    const [editingService, setEditingService] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (userId) {
            fetchServiceConfig();
            if (targetServiceId) {
                fetchServiceDetails(targetServiceId);
            } else {
                fetchServiceDetails('docu_stamp'); // Default fallback
            }
        }
    }, [userId, targetServiceId]);

    const fetchServiceDetails = async (serviceId) => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${userId}/api-services/${serviceId}/details`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            // We can store this in a new state or merge it with existing services state
            // For now, let's keep it simple and store consumption separate or merge
            setServices(prev => ({
                ...prev,
                [serviceId]: {
                    ...prev[serviceId],
                    ...response.data
                }
            }));
        } catch (error) {
            console.error('Error fetching service details:', error);
        }
    };

    const fetchServiceConfig = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${userId}/api-services`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setServices(response.data.services || {});
        } catch (error) {
            console.error('Error fetching service config:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleEditClick = (serviceKey, serviceData) => {
        setEditingService({
            key: serviceKey,
            ...serviceData,
            // Ensure defaults
            enabled: serviceData.enabled || false,
            cost_per_request: serviceData.cost_per_request || 0.40,
            cost_per_document: serviceData.cost_per_document || 0.40,
            free_tier_limit: serviceData.free_tier_limit || 100,
            billing_enabled: serviceData.billing_enabled !== false,
            cost_per_order: serviceData.cost_per_order || 0.25 // Default for bulk orders
        });
    };

    const handleSaveService = async () => {
        if (!editingService) return;
        setSaving(true);
        try {
            const token = localStorage.getItem('token');
            const configToSave = {
                enabled: editingService.enabled,
                cost_per_request: editingService.cost_per_request,
                cost_per_document: editingService.cost_per_document,
                free_tier_limit: editingService.free_tier_limit,
                free_tier_limit: editingService.free_tier_limit,
                billing_enabled: editingService.billing_enabled,
                cost_per_order: editingService.cost_per_order
            };

            await axios.put(
                `${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${userId}/api-services/${editingService.key}`,
                configToSave,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            // Update local state
            setServices(prev => ({
                ...prev,
                [editingService.key]: configToSave
            }));

            setEditingService(null);
            // alert('Service configuration updated successfully!'); // Optional: toast is better if available, or just close modal
        } catch (error) {
            console.error('Error updating service config:', error);
            alert('Failed to update configuration');
        } finally {
            setSaving(false);
        }
    };

    const getServiceName = (key) => {
        const names = {
            'docu_stamp': 'Docu Stamp API',
            'esign': 'eSign API',
            'digisign': 'Digital Signature',
            'bulk_order_management': 'Bulk Order Management',
            'live_stamping': 'Live Stamping API',
            'doqsolv': 'DoqSolv API'
        };
        return names[key] || key;
    };

    const handleToggleService = async (serviceKey, currentStatus) => {
        const action = currentStatus ? 'Deactivate' : 'Activate';
        const confirmMessage = currentStatus
            ? `Are you sure you want to DEACTIVATE this service? This will immediately stop all API access for this client.`
            : `Are you sure you want to ACTIVATE this service? This will enable API access and billing for this client.`;

        if (!window.confirm(confirmMessage)) return;

        try {
            const token = localStorage.getItem('token');
            const service = services[serviceKey];

            await axios.put(
                `${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${userId}/api-services/${serviceKey}`,
                { ...service, enabled: !currentStatus },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            // Update local state
            setServices(prev => ({
                ...prev,
                [serviceKey]: { ...prev[serviceKey], enabled: !currentStatus }
            }));

            // alert(`Service ${action}d successfully`);
        } catch (error) {
            console.error(`Error toggling service:`, error);
            alert(`Failed to ${action} service`);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
        );
    }

    // Define all available services
    const allServices = [
        { key: 'docu_stamp', name: 'Docu Stamp API', icon: '🧩', description: 'Queue-based document retrieval' },
        { key: 'esign', name: 'eSign API', icon: '✍️', description: 'DSC-based electronic signatures' },
        { key: 'digisign', name: 'Digital Signature', icon: '🔐', description: 'Secure digital signing via Email/URL' },
        { key: 'bulk_order_management', name: 'Bulk Order Management', icon: '📦', description: 'High-volume order processing API' },
        { key: 'live_stamping', name: 'Live Stamping API', icon: '⚡', description: 'Real-time single document e-stamping' },
        { key: 'doqsolv', name: 'DoqSolv API', icon: '📄', description: 'Dynamic agreement generation and template management' },
        { key: 'workflows', name: 'Service Workflow Engine', icon: '🔄', description: 'Visual workflow builder and orchestration engine' }
    ];

    const availableServices = targetServiceId
        ? allServices.filter(s => s.key === targetServiceId)
        : allServices;

    return (
        <div className="space-y-8">
            {/* Service Activation Cards */}
            {availableServices.map((svc) => {
                const serviceData = services[svc.key] || {};
                const isEnabled = serviceData.enabled || false;

                return (
                    <div key={svc.key} className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                        {/* Header Row */}
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center">
                                <span className="text-2xl mr-3">{svc.icon}</span>
                                <div>
                                    <h3 className="text-lg font-medium text-gray-900">{svc.name}</h3>
                                    <p className="text-sm text-gray-500">{svc.description}</p>
                                </div>
                            </div>
                            <div className="flex items-center">
                                <span className={`mr-3 text-sm font-medium ${isEnabled ? 'text-green-600' : 'text-gray-500'}`}>
                                    {isEnabled ? 'Active' : 'Inactive'}
                                </span>
                                <button
                                    onClick={() => handleToggleService(svc.key, isEnabled)}
                                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${isEnabled ? 'bg-indigo-600' : 'bg-gray-200'}`}
                                >
                                    <span
                                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${isEnabled ? 'translate-x-5' : 'translate-x-0'}`}
                                    />
                                </button>
                            </div>
                        </div>

                        {/* Expanded Config - Only if enabled */}
                        {isEnabled && (
                            <div className="mt-4 pt-4 border-t border-gray-200">
                                {/* Consumption Stats */}
                                {serviceData.consumption?.current_month && (
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                                        <div className="bg-white p-3 rounded-lg border">
                                            <p className="text-xs text-gray-500">Total Requests</p>
                                            <p className="text-xl font-semibold">{serviceData.consumption.current_month.total_requests || 0}</p>
                                        </div>
                                        <div className="bg-white p-3 rounded-lg border">
                                            <p className="text-xs text-gray-500">Billable</p>
                                            <p className="text-xl font-semibold text-indigo-600">{serviceData.consumption.current_month.billable_requests || 0}</p>
                                        </div>
                                        <div className="bg-white p-3 rounded-lg border">
                                            <p className="text-xs text-gray-500">Documents/Envelopes</p>
                                            <p className="text-xl font-semibold">{serviceData.consumption.current_month.documents_consumed || 0}</p>
                                        </div>
                                        <div className="bg-white p-3 rounded-lg border">
                                            <p className="text-xs text-gray-500">Est. Cost</p>
                                            <p className="text-xl font-semibold">
                                                ₹{typeof serviceData.consumption.current_month.cost === 'number'
                                                    ? serviceData.consumption.current_month.cost.toFixed(2)
                                                    : '0.00'}
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {/* Quick Config Display */}
                                <div className="flex items-center justify-between text-sm">
                                    <div className="flex space-x-4 text-gray-600">
                                        <span>Cost/Req: ₹{serviceData.cost_per_request || 0.40}</span>
                                        <span>Free Limit: {serviceData.free_tier_limit || 100}</span>
                                        <span>Billing: {serviceData.billing_enabled !== false ? '✓' : '✗'}</span>
                                    </div>
                                    <button
                                        onClick={() => handleEditClick(svc.key, serviceData)}
                                        className="text-indigo-600 hover:text-indigo-800 font-medium"
                                    >
                                        Edit Limits
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                );
            })}

            {/* API Key Management - Show if any service is enabled */}
            {Object.values(services).some(s => s?.enabled) && (
                <div className="border-t border-gray-200 pt-6">
                    <APIKeyManagement userId={userId} />
                </div>
            )}


            {/* Edit Service Modal - Sliding Drawer */}
            <Modal
                isOpen={!!editingService}
                onClose={() => setEditingService(null)}
                size="max-w-lg"
            >
                {/* Header */}
                <div className="flex-shrink-0 px-6 py-4 bg-white border-b border-gray-200 flex items-center gap-3">
                    <button
                        onClick={() => setEditingService(null)}
                        className="p-1.5 -ml-1 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-all focus:outline-none"
                        aria-label="Back"
                        title="Close"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                    </button>
                    <h3 className="text-lg font-semibold text-gray-900">
                        Edit {editingService ? getServiceName(editingService.key) : ''}
                    </h3>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    {editingService && (
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Cost per Request (₹)
                                    </label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={editingService.cost_per_request}
                                        onChange={(e) => setEditingService({ ...editingService, cost_per_request: parseFloat(e.target.value) || 0 })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Cost per Document (₹)
                                    </label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={editingService.cost_per_document}
                                        onChange={(e) => setEditingService({ ...editingService, cost_per_document: parseFloat(e.target.value) || 0 })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    />
                                </div>
                                {/* Show Cost per Order only for bulk_order_management */}
                                {editingService.key === 'bulk_order_management' && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Cost per Order (₹)
                                        </label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            value={editingService.cost_per_order}
                                            onChange={(e) => setEditingService({ ...editingService, cost_per_order: parseFloat(e.target.value) || 0 })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                        />
                                    </div>
                                )}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Free Tier Limit
                                    </label>
                                    <input
                                        type="number"
                                        value={editingService.free_tier_limit}
                                        onChange={(e) => setEditingService({ ...editingService, free_tier_limit: parseInt(e.target.value) || 0 })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    />
                                </div>
                                <div className="flex items-center pt-6">
                                    <label className="flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={editingService.billing_enabled}
                                            onChange={(e) => setEditingService({ ...editingService, billing_enabled: e.target.checked })}
                                            className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
                                        />
                                        <span className="ml-2 text-sm text-gray-700">Billing Enabled</span>
                                    </label>
                                </div>
                            </div>

                            <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200">
                                <button
                                    onClick={() => setEditingService(null)}
                                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSaveService}
                                    disabled={saving}
                                    className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
                                >
                                    {saving ? 'Saving...' : 'Save Changes'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </Modal>
        </div>
    );
};

export default APIServiceConfig;
